import { Box, Icon } from "@chakra-ui/react";
import { TbBan, TbShieldCheck, TbWand } from "react-icons/tb";
import { IconType } from "react-icons";

/**
 * StatusPill — bulk-request basket status pill ("Auto-approve" / "Needs
 * approval" / "Rejection").
 * design ref: styles.css:2713 (.ra-status-pill + variants)
 */

export type StatusPillTone = "instant" | "approval" | "rejection";

export interface StatusPillProps {
  tone: StatusPillTone;
  /** Optional label override; defaults match the design copy. */
  label?: string;
}

/* design ref: styles.css:2723–2740 — per-tone palette + icon
   Icons: ti-wand / ti-shield-check / ti-ban → react-icons/tb equivalents. */
const VARIANT: Record<
  StatusPillTone,
  {
    bg: string;
    color: string;
    borderColor: string;
    iconColor: string;
    icon: IconType;
    label: string;
  }
> = {
  instant: {
    bg: "success.50",
    color: "success.700",
    borderColor: "success.200",
    iconColor: "success.600",
    icon: TbWand,
    label: "Auto-approve",
  },
  approval: {
    bg: "warning.50",
    color: "warning.800",
    borderColor: "warning.200",
    iconColor: "warning.700",
    icon: TbShieldCheck,
    label: "Needs approval",
  },
  rejection: {
    bg: "error.50",
    color: "error.700",
    borderColor: "error.200",
    iconColor: "error.600",
    icon: TbBan,
    label: "Rejection",
  },
};

const StatusPill = ({ tone, label }: StatusPillProps) => {
  const v = VARIANT[tone];
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="2xs" // 4px (styles.css:2714)
      // styles.css:2715 — padding: 3px 9px 3px 7px (asymmetric; tighter on
      // left to balance the leading icon). 3/7/9 px aren't theme tokens —
      // inline literals are the spec.
      pt="3px"
      pb="3px"
      pl="7px"
      pr="9px"
      borderRadius="full"
      fontSize="xs" // 12px
      fontWeight={500}
      lineHeight="1"
      whiteSpace="nowrap"
      borderWidth="1px"
      borderStyle="solid"
      flexShrink={0}
      bg={v.bg}
      color={v.color}
      borderColor={v.borderColor}
    >
      <Icon as={v.icon} boxSize="13px" color={v.iconColor} aria-hidden />
      {label ?? v.label}
    </Box>
  );
};

export default StatusPill;
